import { notification } from "antd";
import cuid from "cuid";
import {
  find,
  fromPairs,
  get,
  isEmpty,
  isString,
  keyBy,
  pick,
  without,
} from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { AUTH_PROVIDERS, SCREENS } from "utils/constants";
import { getAuthProvider } from "utils/helpers/authProvider";
import {
  hasIntegrationChanges,
  integrationsChanges,
  integrationsState,
  TELECOM_SYSTEMS,
} from "../Create/onboarding";
import { run, useApiAccess, useEncryption } from "./hooks";
import { ADMIN_TYPES } from "./setup";
import View from "./View";

const USER_FIELDS = ["gender", "firstname", "lastname", "phone", "email"];
const ZITADEL_USER_FIELDS = without(USER_FIELDS, "email");

const telecomsUpdate = (telecoms, existing) =>
  TELECOM_SYSTEMS.filter(
    (system) => !isEmpty(get(telecoms, system)) || existing[system],
  ).map((system) => ({
    id: get(existing, [system, "id"]) || cuid(),
    system,
    value: get(telecoms, system) || "",
  }));

const Detail = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const { id } = useParams();
  const navigate = useNavigate();
  const where = { id };
  const [saving, setSaving] = useState(false);
  const [invitation, setInvitation] = useState({});

  const organizationQuery = useQuery(graphql.queries.ORGANIZATION, {
    variables: { where },
    fetchPolicy: "network-only",
  });
  const integrationsQuery = useQuery(
    graphql.queries.ORGANIZATION_INTEGRATIONS,
    { variables: { where }, fetchPolicy: "network-only" },
  );
  const setupQuery = useQuery(graphql.queries.ORGANIZATION_SETUP, {
    variables: { where },
    fetchPolicy: "network-only",
  });
  const keys = useEncryption({
    where,
    skip: false,
    labels: DETAIL.ENCRYPTION,
    onChanged: () => integrationsQuery.refetch(),
  });
  const api = useApiAccess({ where, skip: false, labels: DETAIL.API });

  const organization = get(organizationQuery, "data.organization");
  const administrator = find(
    get(organization, "users"),
    (user) => get(user, "meta.type") === ADMIN_TYPES[get(organization, "type")],
  );
  const invitationQuery = useQuery(graphql.queries.USER_INVITATION, {
    variables: { where: { id: get(administrator, "id") } },
    skip: !administrator,
    fetchPolicy: "network-only",
  });
  const catalog = get(integrationsQuery, "data.organizationIntegrations") || [];

  const [updateUserAuth] = useMutation(graphql.mutations.UPDATE_USER_AUTH);
  const [updateUser] = useMutation(graphql.mutations.UPDATE_USER);
  const [updateOrganization] = useMutation(
    graphql.mutations.UPDATE_ORGANIZATION,
  );
  const [setIntegrations] = useMutation(
    graphql.mutations.SET_ORGANIZATION_INTEGRATIONS,
  );
  const [generateLink, { loading: copying }] = useMutation(
    graphql.mutations.GENERATE_USER_INVITATION_LINK,
  );
  const [resend, { loading: resending }] = useMutation(
    graphql.mutations.RESEND_USER_INVITATION,
  );

  const existingTelecoms = keyBy(get(organization, "telecoms"), "system");
  const initialValues = {
    organization: {
      name: get(organization, "name") || "",
      type: get(organization, "type"),
      form: get(organization, "form") || "",
      siretNumber: get(organization, "siretNumber") || "",
    },
    telecoms: fromPairs(
      TELECOM_SYSTEMS.map((system) => [
        system,
        get(existingTelecoms, [system, "value"]) || "",
      ]),
    ),
    icon: get(organization, "icon") || null,
    user: fromPairs(
      USER_FIELDS.map((field) => [field, get(administrator, field) || ""]),
    ),
    integrations: integrationsState(catalog),
  };

  const zitadel = getAuthProvider() === AUTH_PROVIDERS.ZITADEL;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const invitationVariables = {
    where: { id: get(administrator, "id") },
    baseUrl,
  };

  const copyLink = () =>
    run(generateLink, { variables: invitationVariables })
      .then(({ generateUserInvitationLink: generated }) =>
        Promise.resolve()
          .then(() => navigator.clipboard.writeText(generated.url))
          .then(
            () => true,
            () => false,
          )
          .then((copied) => setInvitation({ ...generated, copied })),
      )
      .catch(() => notification.error({ message: DETAIL.INVITATION.FAILED }));

  const resendEmail = () =>
    run(resend, { variables: invitationVariables })
      .then(({ resendUserInvitation: sent }) =>
        setInvitation({ ...sent, resent: true }),
      )
      .catch(() => notification.error({ message: DETAIL.INVITATION.FAILED }));

  const onSubmit = async (values) => {
    const user = pick(values.user, zitadel ? ZITADEL_USER_FIELDS : USER_FIELDS);
    const integrations = integrationsChanges(values, initialValues, catalog);
    setSaving(true);
    try {
      if (!isEmpty(administrator)) {
        if (!zitadel)
          await run(updateUserAuth, {
            variables: {
              where: { id: administrator.id },
              data: {
                username: user.email,
                email: user.email,
                phone: user.phone,
              },
            },
          });
        await run(updateUser, {
          variables: { where: { id: administrator.id }, data: user },
        });
      }
      await run(updateOrganization, {
        variables: {
          where,
          data: {
            id,
            name: values.organization.name,
            form: values.organization.form || null,
            siretNumber: values.organization.siretNumber || null,
            telecoms: telecomsUpdate(values.telecoms, existingTelecoms),
            ...(isString(values.icon) ? {} : { icon: values.icon }),
          },
        },
      });
      if (hasIntegrationChanges(integrations))
        await run(setIntegrations, { variables: { where, ...integrations } });
      await Promise.all([
        organizationQuery.refetch(),
        integrationsQuery.refetch(),
        setupQuery.refetch(),
      ]);
      notification.success({ message: DETAIL.SAVED });
    } catch (error) {
      notification.error({ message: DETAIL.FAILED });
    } finally {
      setSaving(false);
    }
  };

  if (!organization || integrationsQuery.loading || keys.loading || api.loading)
    return <Loading />;

  return (
    <View
      initialValues={initialValues}
      catalog={catalog}
      setup={get(setupQuery, "data.organizationSetup") || {}}
      invitation={{
        ...invitation,
        status:
          invitation.status ||
          get(invitationQuery, "data.userInvitation.status"),
        copying,
        resending,
      }}
      encryption={keys.encryption}
      encryptionBusy={keys.busy}
      api={api}
      onLinkKey={keys.onLink}
      onPrepareKeyImport={keys.onPrepare}
      onImportKey={keys.onImport}
      onVerifyKey={keys.onVerify}
      onSendKeyHandover={keys.onSendHandover}
      saving={saving}
      emailReadOnly={zitadel}
      onSubmit={onSubmit}
      onCopyLink={copyLink}
      onResend={resendEmail}
      onCancel={() => navigate(SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.LIST.path)}
      onDelete={() =>
        navigate(`${SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.DELETE.path}/${id}`)
      }
    />
  );
};

export default Detail;
